import { Link, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { LayoutDashboard, BarChart3, FileText, ClipboardList, Settings2, Sparkles as SparklesIcon, Star, Plug as PlugIcon, Compass as CompassIcon } from 'lucide-react'
import { withIcon3D } from '@/components/icons3d/Icon3D'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import { Divider } from '@/components/ui/divider'
import { ShortcutHint } from '@/components/ui/kbd'
import { selectCanManage } from '@/features/auth/authSlice'
import { selectFavorites } from '@/features/favorites/favoritesSlice'
import { selectAssistantVisible, togglePanel } from '@/features/aiChat/panelVisibility'
import { ASK_GRAPHIT } from '@/components/assistant/copy'
import { assistAnchor } from '@/features/assist/anchors'
import { useLinkedRockSetup } from '@/pages/integrations/measurements/setup/useLinkedRockSetup'
import { ROCK_SETUP_PENDING_INDICATOR } from '@/pages/integrations/measurements/setup/copy'
import { FINISH_ROCK_SETUP } from '@/pages/onboarding/rock-setup/copy'
import { rockSetupPath } from '@/pages/onboarding/rock-setup/setupPath'
import { workspaceNavigation, matchesNavigationPath, isNavigationItemActive } from './navigation'

const FAVORITE_TYPES = {
  dashboard: { icon: withIcon3D('dashboards', LayoutDashboard), path: (id) => `/dashboards/${id}` },
  metric: { icon: withIcon3D('metrics', BarChart3), path: (id) => `/metrics/${id}` },
  report: { icon: withIcon3D('reports', FileText), path: (id) => `/reports/${id}` },
  form: { icon: withIcon3D('data-entry', ClipboardList), path: (id) => `/data-entry/${id}/fill` },
}

const Sparkles = withIcon3D('assistant', SparklesIcon)
const Plug = withIcon3D('integrations', PlugIcon)
const Compass = withIcon3D('getting-started', CompassIcon)
const SettingsIcon = withIcon3D('settings', Settings2)

const rowClasses = 'flex h-10 max-sm:min-h-11 [@media(pointer:coarse)]:min-h-11 w-full items-center rounded-control text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-navigation-ink'
const activeClasses = 'bg-navigation-active text-navigation-active-ink font-semibold'
const inactiveClasses = 'text-navigation-ink hover:bg-navigation-active/60 hover:text-navigation-active-ink'

/** Shared desktop rail and phone drawer; task navigation stays in the same order. */
export default function SidebarNav({ showIconsOnly = false, onNavigate }) {
  const { pathname, search } = useLocation()
  const dispatch = useDispatch()
  const canManage = useSelector(selectCanManage)
  const favorites = useSelector(selectFavorites)
  const isAssistantVisible = useSelector(selectAssistantVisible)
  const aiPanelOpen = useSelector((state) => state.aiChat?.panelOpen)
  const { connection: rockConnection, isPending: isRockSetupPending } = useLinkedRockSetup()
  const integrationsActive = matchesNavigationPath(pathname, '/settings/integrations')

  function renderLink(item, { active = matchesNavigationPath(pathname, item.path), nested = false } = {}) {
    const Icon = item.icon
    const link = (
      <Link
        to={item.path}
        state={item.state}
        {...(item.assist ? assistAnchor(item.assist) : {})}
        onClick={onNavigate}
        className={`${rowClasses} ${showIconsOnly ? 'justify-center px-2.5' : nested ? 'gap-2.5 px-3' : 'gap-3 px-3'} ${active ? activeClasses : inactiveClasses}`}
        aria-current={active ? 'page' : undefined}
      >
        <span className="relative shrink-0">
          <Icon className={nested ? 'size-4' : 'size-5'} aria-hidden="true" />
          {item.indicator && <span className="absolute -right-0.5 -top-0.5 size-2 rounded-full bg-warning-solid ring-2 ring-navigation" data-testid="nav-indicator" aria-hidden="true" />}
        </span>
        <span className={showIconsOnly ? 'sr-only' : 'min-w-0 truncate'}>{item.label}</span>
        {item.indicator && <span className="sr-only">{` (${item.indicator})`}</span>}
      </Link>
    )
    return showIconsOnly ? (
      <Tooltip key={item.path}>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right">{item.label}</TooltipContent>
      </Tooltip>
    ) : <div key={item.path}>{link}</div>
  }

  const assistant = (
    <button
      type="button"
      {...assistAnchor('nav.ask_graphit')}
      onClick={() => { onNavigate?.(); dispatch(togglePanel()) }}
      aria-pressed={Boolean(aiPanelOpen)}
      className={`${rowClasses} ${showIconsOnly ? 'justify-center px-2.5' : 'gap-3 px-3'} ${aiPanelOpen ? activeClasses : inactiveClasses}`}
    >
      <Sparkles className="size-5 shrink-0" aria-hidden="true" />
      <span className={showIconsOnly ? 'sr-only' : 'min-w-0 flex-1 truncate text-left'}>{ASK_GRAPHIT}</span>
      {!showIconsOnly && <ShortcutHint keyLabel="J" className="shrink-0 max-md:hidden" />}
    </button>
  )

  return (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-4">
        <div className="space-y-1" role="group" aria-label="Workspace">
          {workspaceNavigation.map((item) => {
            const active = isNavigationItemActive(pathname, item)
            return (
              <div key={item.path}>
                {renderLink(item, { active })}
                {active && item.children && !showIconsOnly && (
                  <div data-shell="subnav" className="ml-5 mt-1 space-y-0.5 border-l border-navigation-line pl-2" role="group" aria-label={`${item.label} navigation`}>
                    {item.children.map((child) => renderLink(child, { nested: true }))}
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {favorites.length > 0 && (
          <div className="mt-8" role="group" aria-label="Favorites">
            {showIconsOnly ? <Divider className="mx-auto mb-2 w-6 bg-navigation-line" /> : (
              <div className="mb-2 flex items-center gap-2 px-3 text-navigation-muted">
                <Star className="size-3.5" aria-hidden="true" />
                <span className="text-caption font-semibold text-navigation-muted">Favorites</span>
              </div>
            )}
            <div className="space-y-1">
              {favorites.map((item) => {
                const type = FAVORITE_TYPES[item.type]
                if (!type) return null
                return renderLink({ path: type.path(item.id), label: item.name || `${item.type} ${item.id}`, icon: type.icon })
              })}
            </div>
          </div>
        )}
      </div>

      <div className="shrink-0 space-y-1 pb-2 pt-2">
        <Divider className="mb-2 bg-navigation-line" />
        {isAssistantVisible && (showIconsOnly ? (
          <Tooltip>
            <TooltipTrigger asChild>{assistant}</TooltipTrigger>
            <TooltipContent side="right">{ASK_GRAPHIT}</TooltipContent>
          </Tooltip>
        ) : assistant)}
        {canManage && <>
          {isRockSetupPending && rockConnection?.id && renderLink({
            path: rockSetupPath(rockConnection.id), label: FINISH_ROCK_SETUP, icon: Plug,
            assist: 'nav.finish_rock_setup', indicator: ROCK_SETUP_PENDING_INDICATOR, state: { from: `${pathname}${search}` },
          })}
          {renderLink({ path: '/settings/integrations', label: 'Integrations', icon: Plug, assist: 'nav.integrations' }, { active: integrationsActive })}
          {renderLink({ path: '/settings', label: 'Settings', icon: SettingsIcon, assist: 'nav.settings' }, { active: matchesNavigationPath(pathname, '/settings') && !integrationsActive })}
          {renderLink({ path: '/getting-started', label: 'Getting started', icon: Compass, assist: 'nav.getting_started' })}
        </>}
      </div>
    </>
  )
}
